import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  FaSearch, FaHotel, FaHome, FaTools, FaMapMarkerAlt, FaLandmark, FaUsers,
  FaUmbrellaBeach, FaArrowRight, FaUserPlus, FaSignInAlt,
  FaTimes, FaBuilding, FaSignOutAlt, FaBed, FaKey, FaShieldAlt, FaHeadset,
  FaCheckCircle, FaWhatsapp, FaFacebookF, FaTwitter, FaInstagram,
  FaLinkedinIn, FaStar, FaChevronDown, FaHandshake, FaMoneyBillWave,
  FaCompass, FaHeart, FaPhone, FaEnvelope, FaMapMarkedAlt, FaCalendarCheck,
  FaClipboardCheck, FaCreditCard, FaGlobe, FaChevronRight, FaMoon, FaSun
} from 'react-icons/fa';
import { IoBedOutline } from 'react-icons/io5';
import { GiHouseKeys } from 'react-icons/gi';
import { MdOutlineRealEstateAgent } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import styles from './ServiceSelector.module.css';
import LogoWhite from '../../assets/logo white.png';
import LogoBlack from '../../assets/logo black.jpg';
import heroImg from '../../assets/cameroon/hero-yaounde-pano.jpg';
import yaoundeImg from '../../assets/cameroon/yaounde-clean.jpg';
import doualaImg from '../../assets/cameroon/douala-aerial2.jpg';
import bueaImg from '../../assets/cameroon/buea-mountain-view.jpg';
import bamendaImg from '../../assets/cameroon/bamenda-avenue.jpg';
import kribiImg from '../../assets/cameroon/kribi-beach.jpg';
import limbeImg from '../../assets/cameroon/limbe-beach.jpg';
import serviceHotelImg from '../../assets/cameroon/service-hotel.jpg';
import serviceSniImg from '../../assets/cameroon/service-sni.jpg';
import serviceGuestHouseImg from '../../assets/cameroon/service-guesthouse.jpg';
import serviceArtisanImg from '../../assets/cameroon/service-artisan.jpg';
import villaImg from '../../assets/cameroon/villa-mandessi.jpg';
import landImg from '../../assets/cameroon/land-plantation.jpg';
import LandingSearchBar from '../LandingSearchBar';
import LanguageSelector from '../LanguageSelector';
import HotelComponent from './listings/hotel/HotelComponent';
import { logoutUser } from '../../api/user/auth';
import { getUserProfile } from '../../api/user/account';
import DashboardShortcuts from '../navbar/DashboardShortcuts';
import { getAllHotels } from '../../api/hotel/hotelApi';
import { getPlatformStats } from '../../api/platformApi';
import useGeolocation from '../../hooks/useGeolocation';
import { useTheme } from '../../context/ThemeContext';
import defaultProfile from '../account-billing/account/default-profile.jpg';
import './listings/hotel/css/Hotel.css';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/* ------------------------------------------------------------------ */
/*  Animated counter (counts up when scrolled into view)               */
/* ------------------------------------------------------------------ */
const Counter = ({ end = 0, suffix = '', duration = 1.4 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const target = Math.max(0, Number(end) || 0);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (target === 0) {
      setValue(0);
      return;
    }
    let start = 0;
    const steps = Math.max(1, Math.floor(duration * 60));
    const increment = target / steps;
    const interval = setInterval(() => {
      start += increment;
      if (start >= target) {
        setValue(target);
        clearInterval(interval);
      } else {
        setValue(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(interval);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className={styles.statNumber}>
      {value}
      {suffix}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Scroll reveal wrapper                                              */
/* ------------------------------------------------------------------ */
const Reveal = ({ children, delay = 0, y = 40 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const ServiceSelector = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const logoSrc = theme === 'dark' ? LogoWhite : LogoBlack;
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [token, setToken] = useState(getCookie('is_logged_in') === 'true');
  const [userName, setUserName] = useState('User');
  const [profileImage, setProfileImage] = useState(defaultProfile);
  const [profileUser, setProfileUser] = useState(null);
  const [nearbyHotels, setNearbyHotels] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [activeTab, setActiveTab] = useState('stay'); // 'stay' | 'services'
  const [scrolled, setScrolled] = useState(false);
  const [platformStats, setPlatformStats] = useState({ hotels: 0, properties: 0, providers: 0 });
  const location = useGeolocation();

  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth' });

  /* ---------------------------- services ---------------------------- */
  const allServices = [
    {
      id: 'hotel', tab: 'stay', title: t('services.book_hotels.title'),
      description: t('services.book_hotels.description'), route: '/hotels',
      icon: <FaHotel />, image: serviceHotelImg,
      accent: '#0f7bcd'
    },
    {
      id: 'guest-house', tab: 'stay', title: t('services.guest_houses.title'),
      description: t('services.guest_houses.description'), route: '/guest-house',
      icon: <IoBedOutline />, image: serviceGuestHouseImg,
      accent: '#10b981'
    },
    {
      id: 'rent', tab: 'stay', title: t('services.rent_properties.title'),
      description: t('services.rent_properties.description'), route: '/rent',
      icon: <GiHouseKeys />, image: serviceSniImg,
      accent: '#f59e0b'
    },
    {
      id: 'buy', tab: 'stay', title: t('services.buy_property.title'),
      description: t('services.buy_property.description'), route: '/buy',
      icon: <MdOutlineRealEstateAgent />, image: villaImg,
      accent: '#d69e2e'
    },
    {
      id: 'coliving', tab: 'stay', title: t('services.coliving.title'),
      description: t('services.coliving.description'), route: '/coliving',
      icon: <FaUsers />, image: limbeImg,
      accent: '#764ba2'
    },
    {
      id: 'land', tab: 'stay', title: t('services.land.title'),
      description: t('services.land.description'), route: '/land',
      icon: <FaLandmark />, image: landImg,
      accent: '#16a34a'
    },
    {
      id: 'services', tab: 'services', title: t('services.service_providers.title'),
      description: t('services.service_providers.description'), route: '/serviceProvider',
      icon: <FaTools />, image: serviceArtisanImg,
      accent: '#0f7bcd'
    },
    {
      id: 'vacation', tab: 'services', title: t('services.vacation_rentals.title'),
      description: t('services.vacation_rentals.description'), route: '/serviceProvider',
      icon: <FaUmbrellaBeach />, image: kribiImg,
      accent: '#10b981'
    }
  ];

  const cities = [
    { name: 'Yaoundé', listings: t('home.cities.listings'), image: yaoundeImg },
    { name: 'Douala', listings: t('home.cities.listings'), image: doualaImg },
    { name: 'Buea', listings: t('home.cities.listings'), image: bueaImg },
    { name: 'Bamenda', listings: t('home.cities.listings'), image: bamendaImg }
  ];

  const steps = [
    { icon: <FaSearch />, title: t('home.steps.step1_title'), desc: t('home.steps.step1_desc') },
    { icon: <FaCalendarCheck />, title: t('home.steps.step2_title'), desc: t('home.steps.step2_desc') },
    { icon: <FaCheckCircle />, title: t('home.steps.step3_title'), desc: t('home.steps.step3_desc') }
  ];

  /* ---------------------------- effects ---------------------------- */
  useEffect(() => {
    setIsVisible(true);
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const handleCookieCheck = () => setToken(getCookie('is_logged_in') === 'true');
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        heroRef.current?.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => heroRef.current?.querySelector('input')?.focus(), 600);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('keydown', handleKeyDown);
    handleCookieCheck();

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Load the logged-in user's profile (name + avatar) for the mobile menu
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUserProfile();
        if (response.success) {
          setProfileUser(response.user);
          const fullName = `${response.user.first_name || ''} ${response.user.last_name || ''}`.trim();
          setUserName(fullName || 'User');
          if (response.user.profile && response.user.profile !== 'default-profile.jpg') {
            setProfileImage(`${response.user.profile}`);
          } else {
            setProfileImage(defaultProfile);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        setProfileImage(defaultProfile);
      }
    };

    if (getCookie('is_logged_in') === 'true') {
      fetchUserData();
    }
  }, []);

  useEffect(() => {
    const fetchNearby = async () => {
      if (location.loaded && location.coordinates?.lat && location.coordinates?.lng) {
        setLoadingNearby(true);
        try {
          const response = await getAllHotels({
            latitude: location.coordinates.lat,
            longitude: location.coordinates.lng,
            radius: 50000,
            limit: 4
          });
          if (response.success) setNearbyHotels(response.data);
        } catch (error) {
          console.error('Error fetching nearby hotels:', error);
        } finally {
          setLoadingNearby(false);
        }
      }
    };
    fetchNearby();
  }, [location.loaded, location.coordinates?.lat, location.coordinates?.lng]);

  useEffect(() => {
    let isMounted = true;
    const fetchStats = async () => {
      const response = await getPlatformStats();
      if (isMounted && response?.success && response?.data) {
        setPlatformStats({
          hotels: Number(response.data.hotels) || 0,
          properties: Number(response.data.properties) || 0,
          providers: Number(response.data.providers) || 0,
        });
      }
    };
    fetchStats();
    return () => { isMounted = false; };
  }, []);

  // Close logout modal on Escape
  useEffect(() => {
    if (!showLogoutConfirm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowLogoutConfirm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLogoutConfirm]);

  /* ---------------------------- handlers ---------------------------- */
  const handleServiceSelect = (route) => {
    navigate(route);
    setIsMobileMenuOpen(false);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirm(false);
    try {
      const response = await logoutUser();
      if (response) {
        document.cookie = "is_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        setToken(false);
        navigate('/login', { replace: true });
        setIsMobileMenuOpen(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    setShowLogoutConfirm(true);
  };

  const filteredServices = allServices.filter((s) => s.tab === activeTab);

  /* ================================================================ */
  return (
    <div className={`${styles.container} ${isVisible ? styles.visible : ''}`}>

      {/* ============================ HEADER ============================ */}
      <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`}>
        <div className={styles.headerContent}>
          {/* Brand */}
          <Link to='/' className={styles.brand} aria-label="Spotfli">
            <img src={logoSrc} alt="Spotfli" className={styles.brandLogo} />
          </Link>

          {/* Navigation */}
          <nav className={styles.navMenu} aria-label="Main navigation">
            <button onClick={() => scrollTo(servicesRef)} className={styles.navLink}>
              {t('options.services')}
            </button>
            <button onClick={() => handleServiceSelect('/hotels')} className={styles.navLink}>
              {t('options.hotel')}
            </button>
            <button onClick={() => handleServiceSelect('/rent')} className={styles.navLink}>
              {t('options.rent')}
            </button>
            <button onClick={() => handleServiceSelect('/buy')} className={styles.navLink}>
              {t('options.buy')}
            </button>
          </nav>

          {/* Actions */}
          <div className={styles.navActions}>
            <button
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark' ? <FaSun /> : <FaMoon />}
            </button>
            <div className={styles.language}><LanguageSelector /></div>
            {token ? (
              <div className={styles.userMenu}>
                <DashboardShortcuts user={profileUser} />
                <button onClick={() => handleServiceSelect('/rent')} className={styles.avatarBtn} title={t('service_selector.browse_properties')}>
                  <FaCompass />
                </button>
                <button onClick={handleLogout} className={styles.avatarBtn} title={t('service_selector.log_out')}>
                  <FaSignOutAlt />
                </button>
              </div>
            ) : (
              <div className={styles.userMenu}>
                <button onClick={() => navigate('/login')} className={styles.loginBtn}>
                  <span>{t('service_selector.login')}</span>
                </button>
                <button onClick={() => navigate('/register')} className={styles.loginBtn}>
                  <span>{t('service_selector.get_started_free')}</span>
                </button>
              </div>
            )}
          </div>

          <button
            className={`${styles.mobileMenuBtn} ${isMobileMenuOpen ? styles.active : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      {/* ========================= MOBILE MENU ========================== */}
      <div ref={mobileMenuRef} className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}>
        <div className={styles.mobileMenuHeader}>
          <span className={styles.mobileMenuBrand}>
            <img src={logoSrc} alt="Spotfli" className={styles.mobileLogo} />
          </span>
          <button className={styles.mobileMenuClose} onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <div className={styles.mobileMenuBody}>
          {/* Profile */}
          {token && (
            <button
              className={styles.mobileProfile}
              onClick={() => { navigate('/accountbilling'); setIsMobileMenuOpen(false); }}
            >
              <img
                src={profileImage}
                alt="Profile"
                className={styles.mobileProfileAvatar}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultProfile;
                }}
              />
              <span className={styles.mobileProfileInfo}>
                <span className={styles.mobileProfileName}>{userName}</span>
                <span className={styles.mobileProfileHint}>{t('sidebar.manage_account')}</span>
              </span>
            </button>
          )}

          {/* Auth */}
          <div className={styles.mobileAuthButtons}>
            {token ? (
              <>
                <DashboardShortcuts user={profileUser} variant="mobile" onNavigate={() => setIsMobileMenuOpen(false)} />
                <button onClick={() => { handleServiceSelect('/rent'); }} className={styles.mobileRegisterBtn}>
                  <FaBuilding /><span>{t('service_selector.browse_properties')}</span>
                </button>
                <button onClick={handleLogout} className={`${styles.mobileLoginBtn} ${styles.mobileLogoutBtn}`}>
                  <FaSignOutAlt /><span>{t('service_selector.log_out')}</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { navigate('/register'); setIsMobileMenuOpen(false); }} className={styles.mobileRegisterBtn}>
                  <FaUserPlus /><span>{t('service_selector.get_started_free')}</span>
                </button>
                <button onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }} className={styles.mobileLoginBtn}>
                  <FaSignInAlt /><span>{t('service_selector.login')}</span>
                </button>
              </>
            )}
          </div>

          {/* Hébergement */}
          <div className={styles.mobileSection}>
            <h3 className={styles.mobileNavTitle}>
              <FaBed /> {t('home.hero.intent_stay')}
            </h3>
            <div className={styles.mobileServices}>
              {allServices.filter((s) => s.tab === 'stay').map((service) => (
                <button key={service.id} className={styles.mobileServiceLink}
                  onClick={() => { handleServiceSelect(service.route); }}>
                  <span className={styles.mobileServiceIcon} style={{ color: service.accent, background: `${service.accent}14` }}>
                    {service.icon}
                  </span>
                  <span className={styles.mobileServiceTitle}>{service.title}</span>
                  <FaChevronRight className={styles.mobileServiceArrow} />
                </button>
              ))}
            </div>
          </div>

          {/* Prestations */}
          <div className={styles.mobileSection}>
            <h3 className={styles.mobileNavTitle}>
              <FaTools /> {t('home.hero.intent_services')}
            </h3>
            <div className={styles.mobileServices}>
              {allServices.filter((s) => s.tab === 'services').map((service) => (
                <button key={service.id} className={styles.mobileServiceLink}
                  onClick={() => { handleServiceSelect(service.route); }}>
                  <span className={styles.mobileServiceIcon} style={{ color: service.accent, background: `${service.accent}14` }}>
                    {service.icon}
                  </span>
                  <span className={styles.mobileServiceTitle}>{service.title}</span>
                  <FaChevronRight className={styles.mobileServiceArrow} />
                </button>
              ))}
            </div>
          </div>

          {/* Langue */}
          <div className={styles.mobileMenuFooter}>
            <span className={styles.mobileFooterLabel}><FaCompass /> {t('options.services')}</span>
            <div className={styles.mobileFooterActions}>
              <button
                className={styles.themeToggle}
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? <FaSun /> : <FaMoon />}
              </button>
              <div className={styles.language}><LanguageSelector /></div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================= HERO ============================= */}
      <section ref={heroRef} className={styles.heroSection}>
        <div className={styles.heroBg}>
          <img
            src={heroImg}
            alt=""
            className={styles.heroBgImage}
          />
          <div className={styles.heroOverlay} />
        </div>

        <motion.div
          className={styles.heroContent}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className={styles.heroChip}>
            <span className={styles.cmFlag} aria-hidden="true"></span>
            <FaMapMarkedAlt /> {t('home.hero.chip')}
          </span>
          <h1 className={styles.heroTitle}>
            {t('service_selector.hero_title')}{' '}
            <span className={styles.heroHighlight}>{t('service_selector.hero_highlight')}</span>
          </h1>
          <div className={styles.heroFlagLine} aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
          <p className={styles.heroSubtitle}>{t('service_selector.hero_subtitle')}</p>

          <div className={styles.searchContainer}>
            <LandingSearchBar />
          </div>

          <div className={styles.heroStats}>
            <div className={styles.statItem}>
              <Counter end={platformStats.hotels} suffix={platformStats.hotels > 0 ? "+" : ""} /><span className={styles.statLabel}>{t('home.stats.hotels')}</span>
            </div>
            <div className={styles.statItem}>
              <Counter end={platformStats.properties} suffix={platformStats.properties > 0 ? "+" : ""} /><span className={styles.statLabel}>{t('home.stats.properties')}</span>
            </div>
            <div className={styles.statItem}>
              <Counter end={platformStats.providers} suffix={platformStats.providers > 0 ? "+" : ""} /><span className={styles.statLabel}>{t('home.stats.providers')}</span>
            </div>
            <div className={styles.statItem}>
              <Counter end={24} suffix="/7" /><span className={styles.statLabel}>{t('home.stats.support')}</span>
            </div>
          </div>
        </motion.div>

        {/* Floating mini-cards */}
        <motion.div
          className={styles.floatCardLeft}
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, duration: 0.9, ease: 'easeOut' }}
        >
          <div className={styles.floatCardIcon}><FaStar /></div>
          <div>
            <strong>{t('home.hero.float_rating')}</strong>
            <span>{t('home.hero.float_rating_label')}</span>
          </div>
        </motion.div>

        <motion.div
          className={styles.floatCardRight}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, duration: 0.9, ease: 'easeOut' }}
        >
          <div className={styles.floatCardIcon}><FaShieldAlt /></div>
          <div>
            <strong>{t('home.hero.float_verified')}</strong>
            <span>{t('home.hero.float_verified_label')}</span>
          </div>
        </motion.div>

        <button onClick={() => scrollTo(servicesRef)} className={styles.scrollIndicator} aria-label="Scroll down">
          <span>{t('service_selector.explore_services')}</span>
          <FaChevronDown />
        </button>
      </section>

      {/* ========================= CITIES SECTION ======================= */}
      <section className={styles.citiesSection}>
        <Reveal>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{t('home.cities.eyebrow')}</span>
            <h2 className={styles.sectionTitle}>{t('home.cities.title')}</h2>
            <p className={styles.sectionSubtitle}>{t('home.cities.subtitle')}</p>
          </div>
        </Reveal>

        <div className={styles.citiesGrid}>
          {cities.map((city, i) => (
            <Reveal key={city.name} delay={i * 0.1}>
              <button className={styles.cityCard} onClick={() => handleServiceSelect('/rent')}>
                <img src={city.image} alt={city.name} loading="lazy" />
                <div className={styles.cityOverlay} />
                <div className={styles.cityInfo}>
                  <span className={styles.cityName}><FaMapMarkerAlt /> {city.name}</span>
                  <span className={styles.cityCount}>{city.listings}</span>
                </div>
                <div className={styles.cityArrow}><FaArrowRight /></div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ======================== SERVICES SECTION ====================== */}
      <section ref={servicesRef} className={styles.servicesSection}>
        <Reveal>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{t('home.services.eyebrow')}</span>
            <h2 className={styles.sectionTitle}>{t('service_selector.section_title')}</h2>
            <p className={styles.sectionSubtitle}>{t('service_selector.section_subtitle')}</p>
          </div>
        </Reveal>

        {/* Tab switcher */}
        <div className={styles.tabs}>
          <span
            aria-hidden="true"
            className={`${styles.tabIndicator} ${activeTab === 'services' ? styles.tabIndicatorRight : ''}`}
          />
          <button
            className={`${styles.tabBtn} ${activeTab === 'stay' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('stay')}
          >
            <FaBed /> {t('home.hero.intent_stay')}
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === 'services' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <FaTools /> {t('home.hero.intent_services')}
          </button>
        </div>

        <motion.div layout className={styles.servicesGrid}>
          <AnimatePresence mode="popLayout">
            {filteredServices.map((service, i) => (
              <motion.div
                layout
                key={service.id}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className={styles.serviceCard}
                onClick={() => handleServiceSelect(service.route)}
              >
                <div className={styles.serviceImageWrap}>
                  <img src={service.image} alt={service.title} loading="lazy" />
                  <div className={styles.serviceImageOverlay} style={{ background: `linear-gradient(180deg, transparent 30%, ${service.accent}22 100%)` }} />
                  <div className={styles.serviceIcon} style={{ background: service.accent }}>
                    {service.icon}
                  </div>
                  <span className={styles.serviceGo}>
                    {t('home.services.explore')} <FaArrowRight />
                  </span>
                </div>
                <div className={styles.serviceBody}>
                  <h3 className={styles.serviceTitle}>{service.title}</h3>
                  <p className={styles.serviceDesc}>{service.description}</p>
                  <span className={styles.serviceStats} style={{ color: service.accent }}>
                    <FaCheckCircle /> {t('home.services.verified')}
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* ======================= HOW IT WORKS =========================== */}
      <section className={styles.howSection}>
        <Reveal>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{t('home.steps.eyebrow')}</span>
            <h2 className={styles.sectionTitle}>{t('home.steps.title')}</h2>
          </div>
        </Reveal>
        <div className={styles.stepsGrid}>
          {steps.map((step, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>0{i + 1}</div>
                <div className={styles.stepIcon}>{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ======================= NEARBY SECTION ========================= */}
      {location.loaded && !location.error && (
        <section className={styles.nearbySection}>
          <Reveal>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionEyebrow}>{t('home.nearby.eyebrow')}</span>
              <h2 className={styles.sectionTitle}>
                <FaMapMarkerAlt className={styles.nearIcon} /> {t('service_selector.nearby_title')}
              </h2>
              <p className={styles.sectionSubtitle}>{t('service_selector.nearby_subtitle')}</p>
            </div>
          </Reveal>

          <div className={styles.nearbyGrid}>
            {loadingNearby ? (
              <div className={styles.loader}>{t('service_selector.loading_nearby')}</div>
            ) : nearbyHotels.length > 0 ? (
              nearbyHotels.map((hotel, index) => (
                <HotelComponent
                  key={`nearby-${hotel._id}`}
                  id={hotel._id}
                  name={hotel.hotelName}
                  images={hotel.images}
                  city={hotel.city}
                  description={hotel.description}
                  rating={hotel.ratings?.average || 0}
                  ratingCount={hotel.ratings?.count || 0}
                  starRating={hotel.starRating}
                  rooms={hotel.rooms}
                  isFirst={index === 0}
                />
              ))
            ) : (
              <div className={styles.noNearby}>{t('service_selector.no_nearby')}</div>
            )}
          </div>
        </section>
      )}

      {/* ========================= TRUST SECTION ======================== */}
      <section className={styles.trustSection}>
        <div className={styles.trustContent}>
          {[
            { icon: <FaMoneyBillWave />, title: t('trust_indicators.secure_payments.title'), desc: t('trust_indicators.secure_payments.description'), color: '#10b981' },
            { icon: <FaHeadset />, title: t('trust_indicators.support.title'), desc: t('trust_indicators.support.description'), color: '#0f7bcd' },
            { icon: <FaShieldAlt />, title: t('trust_indicators.verified_listings.title'), desc: t('trust_indicators.verified_listings.description'), color: '#f59e0b' }
          ].map((item, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={styles.trustItem}>
                <div className={styles.trustIcon} style={{ background: `${item.color}18`, color: item.color }}>
                  {item.icon}
                </div>
                <div className={styles.trustText}>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* =========================== CTA ================================ */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaGlowOne} />
        <div className={styles.ctaGlowTwo} />
        <Reveal>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('service_selector.cta_title')}</h2>
            <p className={styles.ctaDescription}>{t('service_selector.cta_description')}</p>
            <div className={styles.ctaButtons}>
              {token ? (
                <button onClick={() => handleServiceSelect('/rent')} className={styles.ctaPrimary}>
                  <FaBuilding /><span>{t('service_selector.browse_properties')}</span>
                </button>
              ) : (
                <button onClick={() => navigate('/register')} className={styles.ctaPrimary}>
                  <FaUserPlus /><span>{t('service_selector.get_started_free')}</span>
                </button>
              )}
              <button onClick={() => navigate('/login')} className={styles.ctaSecondary}>
                <FaSignInAlt /><span>{t('service_selector.login')}</span>
              </button>
            </div>
            <div className={styles.ctaFeatures}>
              {[
                { icon: <FaCreditCard />, label: t('service_selector.no_credit_card') },
                { icon: <FaClipboardCheck />, label: t('service_selector.free_listings') },
                { icon: <FaHandshake />, label: t('service_selector.instant_access') }
              ].map((f, i) => (
                <div key={i} className={styles.feature}>
                  <div className={styles.featureIcon}>{f.icon}</div>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* =========================== FOOTER ============================= */}
      <footer className={styles.footer}>
        <div className={styles.cmFlagBar} aria-hidden="true">
          <span className={styles.cmBarGreen} />
          <span className={styles.cmBarRed} />
          <span className={styles.cmBarYellow} />
        </div>
        <Reveal>
          <div className={styles.footerContent}>
            <div className={styles.footerBrand}>
              <div className={styles.footerLogoWrap}>
                <img src={logoSrc} alt="Spotfli" className={styles.footerLogo} />
              </div>
              <p className={styles.footerTagline}>{t('service_selector.footer_tagline')}</p>
              <div className={styles.socialLinks}>
                <a href="#" aria-label="Facebook" className={styles.socialLink}><FaFacebookF /></a>
                <a href="#" aria-label="Twitter" className={styles.socialLink}><FaTwitter /></a>
                <a href="#" aria-label="Instagram" className={styles.socialLink}><FaInstagram /></a>
                <a href="#" aria-label="LinkedIn" className={styles.socialLink}><FaLinkedinIn /></a>
              </div>
            </div>

            <div className={styles.footerLinks}>
              <div className={styles.linkGroup}>
                <h4>{t('service_selector.footer_services')}</h4>
                <button className={styles.footerLink} onClick={() => handleServiceSelect('/hotels')}>{t('service_selector.footer_hotels')}</button>
                <button className={styles.footerLink} onClick={() => handleServiceSelect('/rent')}>{t('service_selector.footer_rent')}</button>
                <button className={styles.footerLink} onClick={() => handleServiceSelect('/buy')}>{t('service_selector.footer_buy')}</button>
                <button className={styles.footerLink} onClick={() => handleServiceSelect('/serviceProvider')}>{t('service_selector.footer_service_providers')}</button>
              </div>
              <div className={styles.linkGroup}>
                <h4>{t('service_selector.footer_company')}</h4>
                <button className={styles.footerLink} onClick={() => navigate('/about-us')}>{t('service_selector.footer_about')}</button>
                <button className={styles.footerLink} onClick={() => navigate('/terms-conditions')}>{t('service_selector.terms')}</button>
              </div>
              <div className={styles.linkGroup}>
                <h4>{t('service_selector.footer_support')}</h4>
                <a href="tel:+237676514428" className={styles.footerLink}><FaPhone /> {t('home.footer.phone')}</a>
                <a href="https://miraedge.tech/" target="_blank" rel="noreferrer" className={styles.footerLink}><FaGlobe /> {t('home.footer.email')}</a>
                <a href="https://wa.me/237676514428" target="_blank" rel="noreferrer" className={styles.footerLink}><FaWhatsapp /> WhatsApp</a>
              </div>
            </div>
          </div>
        </Reveal>
        <div className={styles.footerBottom}>
          <p>© {new Date().getFullYear()} Spotfli. {t('service_selector.footer_copyright')}</p>
          <p className={styles.footerMade}><span aria-hidden="true"></span> {t('service_selector.footer_made')}</p>
        </div>
      </footer>

      {/* ======================= WHATSAPP FLOAT ========================= */}
      <a
        href="https://wa.me/237676514428"
        target="_blank"
        rel="noreferrer"
        className={styles.whatsappFloat}
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      {/* ======================= MOBILE BOTTOM NAV ====================== */}
      <nav className={styles.bottomNav} aria-label="Mobile navigation">
        <button className={styles.bottomNavItem} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <FaHome /><span>{t('home.bottom_nav.home')}</span>
        </button>
        <button className={styles.bottomNavItem} onClick={() => scrollTo(heroRef)}>
          <FaSearch /><span>{t('home.bottom_nav.search')}</span>
        </button>
        <button className={styles.bottomNavItem} onClick={() => navigate('/user/bookings')}>
          <FaCalendarCheck /><span>{t('home.bottom_nav.bookings')}</span>
        </button>
        <button className={styles.bottomNavItem} onClick={() => navigate('/accountbilling')}>
          <FaHeart /><span>{t('home.bottom_nav.saved')}</span>
        </button>
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && createPortal(
        <div className={styles.logoutOverlay} onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}>
          <div className={styles.logoutModal}>
            <div className={styles.logoutModalIcon}>
              <FaSignOutAlt size={22} />
            </div>
            <h3 className={styles.logoutModalTitle}>{t('sidebar.logout')}?</h3>
            <p className={styles.logoutModalDesc}>{t('sidebar.logout_confirm', 'Are you sure you want to log out of your account?')}</p>
            <div className={styles.logoutModalActions}>
              <button className={styles.logoutCancelBtn} onClick={() => setShowLogoutConfirm(false)}>
                {t('sidebar.cancel', 'Cancel')}
              </button>
              <button className={styles.logoutConfirmBtn} onClick={handleLogoutConfirm}>
                <FaSignOutAlt size={14} />
                {t('sidebar.logout')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ServiceSelector;
